// ============================================================
// TenantsPage.js
// Lists Tenants with pagination + debounced search. "Create
// Tenant" CTA opens <CreateTenantDialog>.
//
// URL state:
//   ?page=N        1-indexed page (default 1)
//   ?pageSize=N    page size (default 50)
//   ?search=...    free-text filter against TenantName
//
// Row click → /tenants/{tenantId} (TenantDetailPage, added W4
// commit 3/5). The previous workaround was to land on the Clients
// list filtered by Tenant — that surface remains accessible from
// TenantDetailPage's "{N} Clients in this tenant" link.
// ============================================================

import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, Search, X } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import DataTable from '../components/DataTable';
import { ActiveStatusBadge, IdentityProviderBadge } from '../components/StatusBadge';
import CreateTenantDialog, { IDENTITY_PROVIDER_OPTIONS } from './CreateTenantDialog';
import { listTenants } from '../api/tenants';
import { translateError } from '../utils/errorMessages';
import { formatDate } from '../utils/format';
import { toast } from '../components/Toaster';
import './TenantsPage.css';

const DEFAULT_PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;

function parsePageParam(raw, fallback) {
  if (raw == null) return fallback;
  const n = parseInt(raw, 10);
  return Number.isInteger(n) && n > 0 ? n : fallback;
}

export default function TenantsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const page = parsePageParam(searchParams.get('page'), 1);
  const pageSize = parsePageParam(searchParams.get('pageSize'), DEFAULT_PAGE_SIZE);
  const urlSearch = searchParams.get('search') || '';
  // Identity-provider filter (2026-09-04). URL-canonical like search, so
  // "show me the personal-email tenants" is a bookmarkable link — that
  // set is the MSP-conversation list.
  const urlIdp = searchParams.get('identityProvider') || '';

  // Local search input — debounced into the URL search param so
  // URL state stays canonical but typing is responsive.
  const [searchInput, setSearchInput] = useState(urlSearch);
  const debounceRef = useRef(null);

  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  // Keep input in sync with URL (e.g. back/forward navigation).
  useEffect(() => { setSearchInput(urlSearch); }, [urlSearch]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { page, pageSize };
      if (urlSearch) params.search = urlSearch;
      if (urlIdp) params.identityProvider = urlIdp;
      const res = await listTenants(params);
      setItems(res.items || []);
      setTotalCount(res.totalCount || 0);
    } catch (err) {
      setError(translateError(err));
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, urlSearch, urlIdp]);

  function handleIdpChange(value) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value) next.set('identityProvider', value); else next.delete('identityProvider');
      next.delete('page');
      return next;
    });
  }

  useEffect(() => { fetchData(); }, [fetchData]);

  // Debounce search input → URL search param.
  function handleSearchChange(value) {
    setSearchInput(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set('search', value); else next.delete('search');
        next.delete('page'); // reset to page 1 on search change
        return next;
      });
    }, SEARCH_DEBOUNCE_MS);
  }

  function handlePageChange(nextPage) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (nextPage === 1) next.delete('page'); else next.set('page', String(nextPage));
      return next;
    });
  }

  function clearSearch() {
    setSearchInput('');
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('search');
      next.delete('page');
      return next;
    });
  }

  const columns = [
    {
      key: 'tenantName',
      label: 'Tenant Name',
      render: (row) => <span className="tenants-page__name">{row.tenantName}</span>,
    },
    {
      key: 'idp',
      label: 'Identity Provider',
      mobile: 'subtitle',
      render: (row) => <IdentityProviderBadge provider={row.identityProvider} />,
      width: '180px',
    },
    {
      key: 'isActive',
      label: 'Status',
      mobile: 'badge',
      render: (row) => <ActiveStatusBadge isActive={row.isActive} />,
      width: '110px',
    },
    {
      key: 'clientCount',
      label: 'Clients',
      render: (row) => <span className="tenants-page__count">{row.clientCount}</span>,
      width: '90px',
      align: 'right',
    },
    {
      key: 'createdAt',
      label: 'Created',
      render: (row) => <span className="tenants-page__muted">{formatDate(row.createdAt)}</span>,
      width: '140px',
    },
  ];

  function handleRowClick(row) {
    navigate(`/tenants/${row.tenantId}`);
  }

  function handleTenantCreated() {
    fetchData();
  }

  const hasNoResults = !loading && !error && items.length === 0;
  const isFiltered = Boolean(urlSearch) || Boolean(urlIdp);

  return (
    <>
      <PageHeader
        title="Tenants"
        subtitle="Identity tenants used by CareHub clients."
        cta={
          <button
            type="button"
            className="tenants-page__create"
            onClick={() => setIsCreateOpen(true)}
          >
            <Plus size={16} />
            Create Tenant
          </button>
        }
      />

      <div className="tenants-page__toolbar">
        <div className="tenants-page__search">
          <Search size={16} className="tenants-page__search-icon" />
          <input
            type="search"
            placeholder="Search tenants…"
            value={searchInput}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="tenants-page__search-input"
          />
          {searchInput && (
            <button
              type="button"
              className="tenants-page__search-clear"
              onClick={clearSearch}
              aria-label="Clear search"
            >
              <X size={14} />
            </button>
          )}
        </div>

        <label className="tenants-page__filter">
          <span className="tenants-page__filter-label">Identity provider</span>
          <select
            value={urlIdp}
            onChange={(e) => handleIdpChange(e.target.value)}
            className="tenants-page__filter-select"
          >
            <option value="">All providers</option>
            {IDENTITY_PROVIDER_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </label>
      </div>

      <DataTable
        data={items}
        columns={columns}
        rowKey={(row) => row.tenantId}
        onRowClick={handleRowClick}
        totalCount={totalCount}
        page={page}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        loading={loading}
        error={error}
        onRetry={fetchData}
        emptyMessage={isFiltered ? 'No tenants match your filters.' : 'No Tenants yet.'}
        emptyAction={
          !isFiltered && hasNoResults ? (
            <button
              type="button"
              className="tenants-page__create-empty"
              onClick={() => setIsCreateOpen(true)}
            >
              <Plus size={16} />
              Create your first Tenant
            </button>
          ) : null
        }
      />

      <CreateTenantDialog
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        onCreated={handleTenantCreated}
      />
    </>
  );
}
